/* eslint-disable no-shadow */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'
import { ButtonPrimaryCSS } from 'components/elements/Buttons/ButtonPrimary'
import { ButtonSecondaryCSS } from 'components/elements/Buttons/ButtonSecondary'

// Components
import FormDuuf, {
  FormDuufGroup,
  FormDuufSubmit,
  MAX_UPLOAD_FILE_BYTES,
  PHP_SAFE_UPLOAD_BYTES,
  useFormDuuf,
} from 'components/related/Form'

// Interface
import { FormProps } from 'components/flex/Form/FormShell'

const StyledFormDuuf = styled(FormDuuf)`
  position: relative;
  background: ${({ theme }) => theme.color.light};

  & .form-duuf-group {
    margin-bottom: 2rem;
  }

  & .form-duuf-textarea,
  & .form-duuf-input {
    width: 100%;
    border-color: #f2f2f2;
    border-style: solid;
    border-width: 1px;
    min-height: 38px;
    padding: 10px 20px;
    background-color: #f2f2f2;
  }

  & .form-duuf-textarea {
    height: 100px;
  }

  & .form-duuf-file-button {
    ${ButtonSecondaryCSS};
  }

  & .form-duuf-file-name {
    margin-left: 0.5rem;
  }

  & .form-duuf-checkbox-label {
    margin-left: 0.5rem;
    margin-bottom: 0;
  }

  & .form-duuf-label-mandatory {
    color: ${({ theme }) => theme.color.secondary};
  }

  & .form-duuf-error {
    color: tomato;
  }

  & .form-duuf-submit {
    ${ButtonPrimaryCSS};

    &-disabled {
      opacity: 0.5;
    }
  }

  & .form-duuf-confirm {
    color: green;
  }

  & .form-duuf-something-wrong {
    color: red;
  }

  & .form-duuf-loading {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.5);
  }
`

const FormWrapper = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.color.light};
`

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    font-size: 36px;
    & strong {
      font-weight: ${({ theme }) => theme.font.weight.extraBold};
    }
  }
`

const Title = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    opacity: 0.1;
    & strong {
      font-weight: ${({ theme }) => theme.font.weight.extraBold};
    }
    @media (min-width: 1400px) {
      font-size: 100px;
    }
    @media (max-width: 1399px) {
      font-size: 80px;
    }
    @media (max-width: 1199px) {
      font-size: 70px;
    }
    @media (max-width: 767px) {
      font-size: 40px;
    }
    @media (max-width: 575px) {
      font-size: 30px;
    }
  }
`

const Section = styled.section`
  @media (min-width: 992px) {
    margin-bottom: 4rem;
  }
`

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const DRAFT_STORAGE_KEY = 'kokall-form-4-draft'
const MAX_FILE_SIZE = MAX_UPLOAD_FILE_BYTES
const MAX_TOTAL_UPLOAD_BYTES = PHP_SAFE_UPLOAD_BYTES
const UPLOAD_FIELD_NAMES = ['input_17', 'input_20', 'input_21']
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'docx', 'heic', 'heif']
const ACCEPT_TYPES = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')

// const CheckWrapper = styled.div`
//   font-size: 20px;
//   font-weight: 700;
//   font-family: ${({ theme }) => theme.font.family.primary};
//   color: ${({ theme }) => theme.color.primary};
// `

const FormInvoice: React.FC<FormProps> = ({ fields }) => (
  <Section id={fields.sectionid}>
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="row">
            <div className="col-lg-12">
              {/* <Title content={fields.formdescription} className="mb-3" /> */}

              <FormWrapper className="p-lg-5 p-4">
                <Content content={fields.formdescription} className="mb-5" />
                <StyledFormDuuf
                  id={4}
                  generate={false}
                  onSubmitDone={() => {
                    try {
                      sessionStorage.removeItem(DRAFT_STORAGE_KEY)
                    } catch {
                      // Ignore storage errors (private mode)
                    }
                  }}
                >
                  <InnerForm />
                </StyledFormDuuf>
              </FormWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
)

function InnerForm() {
  const { formik } = useFormDuuf()
  const [draftReady, setDraftReady] = useState(false)

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY)

      if (raw) {
        const saved = JSON.parse(raw) as Record<string, string>
        const next = { ...formik.values }
        const touched: Record<string, boolean> = { ...formik.touched }

        Object.keys(saved).forEach((key) => {
          if (typeof saved[key] === 'string' && saved[key]) {
            next[key] = saved[key]
            touched[key] = true
          }
        })

        formik.setValues(next)
        formik.setTouched(touched, true)
      }
    } catch {
      // Ignore invalid or unavailable storage
    } finally {
      setDraftReady(true)
    }
  }, [])

  useEffect(() => {
    if (!draftReady) {
      return
    }

    try {
      const toSave: Record<string, string> = {}

      Object.entries(formik.values).forEach(([key, value]) => {
        if (typeof value === 'string') {
          toSave[key] = value
        }
      })

      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(toSave))
    } catch {
      // Ignore storage errors (private mode)
    }
  }, [draftReady, formik.values])

  return (
    <>
      <div className="row">
        <div className="col-lg-6">
          <FormDuufGroup showIds={[1]} />
        </div>
        <div className="col-lg-6">
          <FormDuufGroup showIds={[3]} />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-6">
          <FormDuufGroup showIds={[15]} />
        </div>
        <div className="col-lg-6">
          <FormDuufGroup showIds={[16]} />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-6">
          <FormDuufGroup showIds={[13]} />
        </div>
        <div className="col-lg-6">
          <FormDuufGroup showIds={[4]} />
        </div>
      </div>
      <div className="row">
        <FormDuufGroup showIds={[7]} />
      </div>
      <div className="row">
        <FormDuufGroup showIds={[8]} />
      </div>
      <div className="row">
        <p>
          Wilt u uw aanvraag verduidelijken met tekeningen, foto’s of
          plattegronden? Voeg deze dan hieronder toe. (maximaal 3 bestanden, max
          10MB per stuk)
        </p>
        <CustomUploadField name="input_17" />
        <CustomUploadField name="input_20" />
        <CustomUploadField name="input_21" />
      </div>
      <div className="row">
        <div className="col-lg-6">
          <FormDuufSubmit />
        </div>
      </div>
    </>
  )
}

function getFileExtension(fileName: string) {
  const parts = fileName.split('.')
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : ''
}

function getTotalUploadBytes(
  values: Record<string, unknown>,
  nextFile: File,
  currentFieldName: string
) {
  return UPLOAD_FIELD_NAMES.reduce((total, key) => {
    const value = key === currentFieldName ? nextFile : values[key]
    return value instanceof File ? total + value.size : total
  }, 0)
}

function prepareUploadFile(file: File) {
  const extension = getFileExtension(file.name)

  if (extension === 'jpeg') {
    return new File([file], file.name.replace(/\.jpeg$/i, '.jpg'), {
      type: file.type || 'image/jpeg',
    })
  }

  if (extension === 'heif') {
    return new File([file], file.name.replace(/\.heif$/i, '.heic'), {
      type: file.type || 'image/heic',
    })
  }

  return file
}

function CustomUploadField({ name }: { name: string }) {
  const { formik } = useFormDuuf()
  const [fileName, setFileName] = useState('')
  const inputRef: React.RefObject<HTMLInputElement> = React.useRef(null)
  const [error, setError] = useState<string | null>(null)

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const clearFile = () => {
    setFileName('')
    setError(null)
    resetInput()
    formik.setFieldValue(name, '')
  }

  return (
    <div className="form-duuf-group form-duuf-group-fileupload position-relative">
      <div className="form-duuf-file d-flex align-items-center flex items-center">
        <HiddenFileInput
          onChange={(e) => {
            const selected = e.target.files?.[0]
            setError(null)

            if (!selected) {
              return
            }

            const extension = getFileExtension(selected.name)

            if (!ALLOWED_EXTENSIONS.includes(extension)) {
              setError(
                'Dit bestandstype is niet toegestaan. Gebruik jpg, png, pdf, docx of heic.'
              )
              resetInput()
              formik.setFieldValue(name, '')
              setFileName('')
              return
            }

            if (selected.size > MAX_FILE_SIZE) {
              setError('Bestandsgrootte mag niet groter zijn dan 10MB')
              resetInput()
              formik.setFieldValue(name, '')
              setFileName('')
              return
            }

            if (
              getTotalUploadBytes(formik.values, selected, name) >
              MAX_TOTAL_UPLOAD_BYTES
            ) {
              setError(
                'Alle bestanden samen zijn te groot. Verklein een bestand of upload er minder.'
              )
              resetInput()
              formik.setFieldValue(name, '')
              setFileName('')
              return
            }

            const file = prepareUploadFile(selected)
            setFileName(file.name)
            formik.setFieldValue(name, file)
            formik.setFieldTouched(name, true, false)
          }}
          type="file"
          id={name}
          accept={ACCEPT_TYPES}
          ref={inputRef}
        />
        <span className="form-duuf-file-button">
          <button
            type="button"
            onClick={() => {
              inputRef?.current?.click()
            }}
          >
            Kies een bestand
          </button>
        </span>
        {fileName && (
          <>
            <span className="form-duuf-file-name">{fileName}</span>
            <button
              type="button"
              className="ms-2"
              onClick={clearFile}
              aria-label="Bestand verwijderen"
            >
              ×
            </button>
          </>
        )}
        {(error || formik.errors[name]) && (
          <span className="form-duuf-error ms-2">
            {error || String(formik.errors[name])}
          </span>
        )}
      </div>
    </div>
  )
}

export default FormInvoice
